import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Download, RefreshCw } from 'lucide-react';

import { DataTable } from '@/components/data-table/DataTable';
import { Button } from '@/components/ui/button';
import { getApiCall } from '@/service/getApiCall';
import { API_DELETED_FILES, API_DELETED_FILE_TYPES } from '@/service/url';
import { buildStorageUrl } from '@/utilities/storageUrl';
import { exportToCSV } from '@/utilities/helpers';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

type FileType = { type: string; label: string; deleted_at_exact: boolean };

type DeletedFile = {
  type: string;
  type_label: string;
  record_id: number;
  name: string | null;
  detail: string | null;
  file_name: string | null;
  blob_path: string | null;
  deleted_at: string | null;
  deleted_at_exact: boolean;
  deleted_by: string | null;
  owner: string | null;
};

const ALL = 'all';

/** `YYYY-MM-DD` in local time. `toISOString()` would shift the day in IST. */
function asDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

const today = () => asDay(new Date());
const firstOfThisMonth = () => {
  const now = new Date();
  return asDay(new Date(now.getFullYear(), now.getMonth(), 1));
};

/* A container-qualified path is one the storage gateway can serve. */
const isQualified = (blobPath: string | null) =>
  /^(cp-\d+-[a-z0-9-]+|sa-[a-z0-9-]+)\//.test(String(blobPath || '').trim());

function formatWhen(value: string | null): string {
  if (!value) return '—';

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

export default function DeletedFilesTab() {
  const [fromDate, setFromDate] = useState(firstOfThisMonth);
  const [toDate, setToDate] = useState(today);
  const [type, setType] = useState(ALL);
  const [types, setTypes] = useState<FileType[]>([]);
  const [rows, setRows] = useState<DeletedFile[]>([]);
  const [notes, setNotes] = useState<string[]>([]);
  const [unavailable, setUnavailable] = useState<string[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getApiCall(API_DELETED_FILE_TYPES, {})
      .then((data: any) => setTypes(Array.isArray(data?.types) ? data.types : []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const data: any = await getApiCall(API_DELETED_FILES, {
        from: fromDate,
        to: toDate,
        types: type === ALL ? undefined : type,
      });

      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setNotes(Array.isArray(data?.notes) ? data.notes : []);
      setUnavailable(Array.isArray(data?.unavailable) ? data.unavailable : []);
      setTruncated(data?.truncated === true);
      setTotal(Number(data?.total) || 0);

      // The window the backend used, which may be a clamped version of ours.
      if (data?.from_date) setFromDate(data.from_date);
      if (data?.to_date) setToDate(data.to_date);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, type]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const columns = useMemo<ColumnDef<DeletedFile>[]>(
    () => [
      {
        accessorKey: 'deleted_at',
        header: 'Deleted',
        cell: ({ row }: any) => (
          <span className="whitespace-nowrap">
            {formatWhen(row.original.deleted_at)}
            {!row.original.deleted_at_exact && (
              <span
                className="ml-1.5 text-[11px] text-muted-foreground"
                title="This table does not store a deletion time. Shown is the row's last update, which for a deleted row is when it was deleted."
              >
                approx.
              </span>
            )}
          </span>
        ),
      },
      { accessorKey: 'type_label', header: 'Type' },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }: any) => (
          <span>
            {row.original.name || '—'}
            {row.original.detail && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                ({row.original.detail})
              </span>
            )}
          </span>
        ),
      },
      {
        accessorKey: 'file_name',
        header: 'File',
        cell: ({ row }: any) => {
          const { blob_path: blobPath, file_name: fileName, type: assetType } = row.original;

          if (!blobPath) {
            return (
              <span className="text-xs text-muted-foreground" title="No file was stored for this record">
                {fileName || 'no file stored'}
              </span>
            );
          }

          const url = isQualified(blobPath) ? buildStorageUrl(assetType as any, blobPath) : null;

          if (!url) {
            /* A legacy path with no container in it - the file is there, but
               which container it sits in cannot be told from the row alone. */
            return (
              <span className="break-all text-xs" title={blobPath}>
                {fileName || blobPath}
              </span>
            );
          }

          return (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="break-all text-xs text-primary hover:underline"
              title={blobPath}
            >
              {fileName || blobPath}
            </a>
          );
        },
      },
      { accessorKey: 'record_id', header: 'Record' },
      {
        accessorKey: 'deleted_by',
        header: 'Deleted by',
        cell: ({ row }: any) => row.original.deleted_by || '—',
      },
      {
        accessorKey: 'owner',
        header: 'Owner',
        cell: ({ row }: any) => row.original.owner || '—',
      },
    ],
    [],
  );

  const handleExport = () =>
    exportToCSV(
      rows.map((row) => ({
        Deleted: formatWhen(row.deleted_at),
        Exact: row.deleted_at_exact ? 'yes' : 'approx',
        Type: row.type_label,
        Name: row.name || '',
        Detail: row.detail || '',
        File: row.file_name || '',
        'Blob path': row.blob_path || '',
        Record: row.record_id,
        'Deleted by': row.deleted_by || '',
        Owner: row.owner || '',
      })),
      `deleted-files-${fromDate}-to-${toDate}`,
    );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          From
          <CalendarDateInput
            value={fromDate}
            onChange={(event) => setFromDate(event)}
            max={toDate}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          To
          <CalendarDateInput
            value={toDate}
            onChange={(event) => setToDate(event)}
            min={fromDate}
            max={today()}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
          File type
          <select
            value={type}
            onChange={(event) => setType(event.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
          >
            <option value={ALL}>All types</option>
            {types.map((option) => (
              <option key={option.type} value={option.type}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <Button size="sm" variant="outline" className="gap-1.5" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Apply
        </Button>

        {rows.length > 0 && (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={handleExport}>
            <Download className="h-3.5 w-3.5" />
            Export
          </Button>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Deleted records only.
      </p>

      {notes.map((note) => (
        <p key={note} className="text-xs text-amber-600 dark:text-amber-400">
          {note}
        </p>
      ))}

      {unavailable.length > 0 && (
        <p className="text-xs text-rose-600 dark:text-rose-400">
          Could not read: {unavailable.join(', ')}. The rest of the list is complete.
        </p>
      )}

      {truncated && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          Showing the {rows.length} most recent of {total}. Narrow the range or pick one type to
          see the rest.
        </p>
      )}

      <div className="scroll-container">
        <DataTable
          tableId="deleted-files:v1"
          columns={columns}
          data={rows}
          pageSize={25}
          emptyTitle={loading ? 'Loading…' : 'No deleted files in this range'}
          disableExport
        />
      </div>
    </div>
  );
}
